
import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle, TrendingUp } from "lucide-react";
import type { MortgageData } from "../../CalculatorTabs";
import { AutoCalculateOptions } from "../../utils/types";

interface CreditScoreSelectorProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
  onCreditScoreChange: (value: number) => void;
}

const CreditScoreSelector: React.FC<CreditScoreSelectorProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate,
  onCreditScoreChange
}) => {
  const creditScoreBands = [
    { range: "580-619", value: 600, label: "Fair Credit", color: "orange", impact: "Higher rates, limited options" },
    { range: "620-659", value: 640, label: "Good Credit", color: "yellow", impact: "Moderate rates, FHA eligible" },
    { range: "660-719", value: 690, label: "Very Good", color: "blue", impact: "Good rates, most programs" },
    { range: "720-759", value: 740, label: "Excellent", color: "green", impact: "Great rates, premium options" },
    { range: "760+", value: 780, label: "Exceptional", color: "purple", impact: "Best rates, all programs" },
  ];

  const getCurrentBand = () => {
    return creditScoreBands.find(band => 
      band.value === mortgageData.creditScore
    ) || creditScoreBands[2]; // Default to Very Good
  };

  const handleCreditScoreChange = (value: string) => {
    const numericValue = parseInt(value);
    onCreditScoreChange(numericValue);
  };

  const getLoanEligibilityAlerts = () => {
    const currentScore = mortgageData.creditScore;
    const alerts = [];

    if (mortgageData.loanType === 'conventional' && currentScore < 620) {
      alerts.push({
        type: 'warning' as const,
        message: 'Conventional loans typically require 620+ credit score'
      });
    }

    if (mortgageData.loanType === 'fha' && currentScore < 580) {
      alerts.push({
        type: 'error' as const,
        message: 'FHA loans require minimum 580 credit score'
      });
    }

    if (mortgageData.loanType === 'va' && currentScore < 580) {
      alerts.push({
        type: 'warning' as const,
        message: 'VA loans prefer 580+ credit score for better terms'
      });
    }

    if (currentScore >= 740) {
      alerts.push({
        type: 'success' as const,
        message: 'Excellent credit! You qualify for the best rates'
      });
    }

    return alerts;
  };

  const currentBand = getCurrentBand();
  const eligibilityAlerts = getLoanEligibilityAlerts();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">Credit Score Range</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p className="text-sm">
                Your credit score significantly impacts your interest rate and loan eligibility. 
                Select the range that best matches your current score.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TrendingUp className="w-4 h-4 text-muted-foreground" />
      </div>

      <Select 
        value={mortgageData.creditScore.toString()}
        onValueChange={handleCreditScoreChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select your credit score range" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {creditScoreBands.map((band) => (
            <SelectItem key={band.value} value={band.value.toString()}>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <span className="font-medium">{band.range}</span>
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${
                      band.color === 'orange' ? 'border-orange-500 text-orange-600' :
                      band.color === 'yellow' ? 'border-yellow-500 text-yellow-600' :
                      band.color === 'blue' ? 'border-blue-500 text-blue-600' :
                      band.color === 'green' ? 'border-green-500 text-green-600' :
                      'border-purple-500 text-purple-600'
                    }`}
                  >
                    {band.label}
                  </Badge>
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Current Selection Info */}
      <div className="p-3 bg-muted/30 rounded-lg border">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">Selected: {currentBand.range}</span>
          <Badge 
            variant="outline" 
            className={`text-xs ${
              currentBand.color === 'orange' ? 'border-orange-500 text-orange-600' :
              currentBand.color === 'yellow' ? 'border-yellow-500 text-yellow-600' :
              currentBand.color === 'blue' ? 'border-blue-500 text-blue-600' :
              currentBand.color === 'green' ? 'border-green-500 text-green-600' :
              'border-purple-500 text-purple-600'
            }`}
          >
            {currentBand.label}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground">
          {currentBand.impact}
        </div>
      </div>

      {/* Eligibility Alerts */}
      {eligibilityAlerts.map((alert, index) => (
        <div 
          key={index}
          className={`text-xs p-2 rounded-lg border ${
            alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' :
            alert.type === 'warning' ? 'bg-orange-50 border-orange-200 text-orange-700' :
            'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {alert.message}
        </div>
      ))}
    </div>
  );
};

export default CreditScoreSelector;
